import React from 'react';
import {
  StepIndicator as KitStepIndicator,
  Step,
  type StepStatus,
} from '@/design-system/step-indicator';
import { cn } from '@/design-system/lib/utils';

interface StepIndicatorProps {
  steps: Array<{
    id: number;
    label: string;
    completed?: boolean;
    /** Second line under the label (vertical form). */
    description?: React.ReactNode;
    /** The flow stopped here (failed / cancelled): the marker turns red. */
    failed?: boolean;
  }>;
  currentStep: number;
  /**
   * `vertical` stacks the steps as a flow card (label + description each).
   * `horizontal` (default) is the inline wizard stepper.
   */
  orientation?: 'horizontal' | 'vertical';
  /**
   * Narrow-viewport form of the same state (DD11): one line
   * "Langkah 3/5 · Biaya" over a progress rule, instead of the full stepper
   * whose labels cannot survive below ~640px. Same `aria-current="step"`
   * semantics, so assistive tech reads the same thing either way.
   */
  compact?: boolean;
  /** Overrides the default "Langkah {n}/{total} · {label}" line. */
  compactLabel?: (current: number, total: number, label: string) => string;
  className?: string;
}

const StepIndicator: React.FC<StepIndicatorProps> = ({
  steps,
  currentStep,
  orientation = 'horizontal',
  compact = false,
  compactLabel,
  className,
}) => {
  const getStepStatus = (stepId: number): StepStatus => {
    if (currentStep > stepId) return 'completed';
    if (currentStep === stepId) return 'current';
    return 'upcoming';
  };

  if (compact) {
    const index = Math.max(
      0,
      steps.findIndex((s) => s.id === currentStep)
    );
    const current = steps[index];
    const pct = steps.length ? ((index + 1) / steps.length) * 100 : 0;
    const label = compactLabel
      ? compactLabel(index + 1, steps.length, current?.label ?? '')
      : `Langkah ${index + 1}/${steps.length} · ${current?.label ?? ''}`;
    return (
      <div className={cn('w-full', className)}>
        <p
          aria-current="step"
          className="mb-2 text-[12px] font-semibold tracking-tight text-text-strong-950"
        >
          {label}
        </p>
        <div
          className="h-1 w-full overflow-hidden rounded-full bg-bg-weak-50"
          role="progressbar"
          aria-valuenow={index + 1}
          aria-valuemin={1}
          aria-valuemax={steps.length}
        >
          <span
            className="block h-full rounded-full bg-primary-base transition-[width] duration-(--duration-fast)"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    );
  }

  return (
    <KitStepIndicator orientation={orientation} className={className}>
      {steps.map((step, index) => {
        const status = getStepStatus(step.id);

        return (
          <Step
            key={step.id}
            status={status}
            index={step.id - 1}
            orientation={orientation}
            label={
              step.failed ? (
                <>
                  {step.label}
                  <span className="sr-only"> (gagal)</span>
                </>
              ) : (
                step.label
              )
            }
            description={step.description}
            aria-current={status === 'current' ? 'step' : undefined}
            withConnector={
              orientation === 'horizontal' && index < steps.length - 1
            }
            // Kit greys completed + upcoming labels to sub-600; fade upcoming
            // one step further so unreached steps clearly recede.
            className={cn(
              status === 'upcoming' && '[&>div>span]:text-text-soft-400',
              step.failed &&
                '[&>span:first-child]:border-transparent [&>span:first-child]:bg-(--state-error-base) [&>span:first-child]:text-static-white'
            )}
          />
        );
      })}
    </KitStepIndicator>
  );
};

export default StepIndicator;
